import { FC } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { Alert, Box, Breadcrumbs, Divider, Stack } from '@mui/material';
import { MainContentPanel } from '../components/MainContentPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import { GET_SINGLE_ADFS_BY_BKS_ID } from '../administration/ADFSConfig/ADFSConfig.graphql';
import { GET_IDENTITY_PROVIDER_SUMMARY } from './Sso.graphql';
import SsoStatus from './SsoStatus';
import SsoLoginSection from './SsoLoginSection';
import SsoSeatsSection from './SsoSeatsSection';
import SsoSyncSection from './SsoSyncSection';
import SsoClientAuthorizationSection from './SsoClientAuthorizationSection';
import DeleteAdfs from '../administration/ADFSConfig/DeleteAdfs';

export default function SsoPage() {
  const params = useParams<{ id: string }>();
  const bksID = params.id ? parseInt(params.id) : NaN;
  const navigate = useNavigate();

  const { data: adfsData, loading: adfsLoading } = useQuery(GET_SINGLE_ADFS_BY_BKS_ID, {
    variables: { bksID },
    skip: isNaN(bksID),
    fetchPolicy: 'cache-and-network',
  });
  const { data: summaryData, loading: summaryLoading } = useQuery(GET_IDENTITY_PROVIDER_SUMMARY, {
    variables: { bksID },
    skip: isNaN(bksID),
    fetchPolicy: 'cache-and-network',
  });

  const adfs = adfsData?.adfs;
  const summary = summaryData?.summary;

  if ((adfsLoading && !adfs) || (summaryLoading && !summary)) return <LoadingSpinner />;

  if (!adfs || !summary) {
    return (
      <MainContentPanel>
        <Alert severity="info">
          Lisensen har ikke firmapålogging. <Link to={`/license/${params.id}`}>Tilbake til kunden</Link>
        </Alert>
      </MainContentPanel>
    );
  }

  return (
    <MainContentPanel>
      <Breadcrumbs sx={{ mb: 1, fontSize: 13 }}>
        <Link to="/idp">Firmapålogging</Link>
        <Link to={`/license/${bksID}`}>{summary.companyName ?? `Lisens ${bksID}`}</Link>
      </Breadcrumbs>
      <Stack spacing={3} divider={<Divider flexItem />}>
        <SsoStatus summary={summary} />
        <SsoLoginSection adfs={adfs} summary={summary} />
        <SsoSeatsSection summary={summary} />
        <SsoSyncSection bksID={bksID} summary={summary} />
        <SsoClientAuthorizationSection adfs={adfs} />
        <Box>
          <DeleteAdfs labelled licenseId={bksID} provider={summary.scheme} onDeleted={() => navigate('/idp')} />
        </Box>
      </Stack>
    </MainContentPanel>
  );
}

/** The old address; kept so bookmarks still land. */
export const LegacySsoRedirect: FC = () => {
  const { bksID } = useParams<{ bksID: string }>();
  return <Navigate to={`/license/${bksID}/idp`} replace />;
};
